import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../services/api';
import { useAuth } from './AuthContext';

export interface EsscoProduct {
  id: string;
  esscoId: string;
  name: string;
  description: string | null;
  price: number | null;
  unit: string;
  isInStock: boolean;
  isFavorite: boolean;
  categories: Array<{ id: number | string; name: string }>;
  imageUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

interface EsscoCatalogContextType {
  products: EsscoProduct[];
  loading: boolean;
  error: string | null;
  searchProducts: (query: string) => EsscoProduct[];
  getFavorites: () => EsscoProduct[];
  updateProduct: (id: string, data: Partial<Pick<EsscoProduct, 'esscoId' | 'name' | 'description' | 'price' | 'unit' | 'isInStock' | 'isFavorite'>>) => Promise<EsscoProduct>;
  duplicateProduct: (id: string) => Promise<EsscoProduct>;
  toggleFavorite: (id: string) => Promise<void>;
  deleteProduct: (id: string) => Promise<void>;
  refreshCatalog: () => Promise<void>;
  getProductById: (id: string) => EsscoProduct | undefined;
  getProductByEsscoId: (esscoId: string) => EsscoProduct | undefined;
}

const EsscoCatalogContext = createContext<EsscoCatalogContextType | null>(null);

export function EsscoCatalogProvider({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const [products, setProducts] = useState<EsscoProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCatalog = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.getEsscoCatalog();
      setProducts(response.products || []);
    } catch (err: any) {
      console.error('Failed to fetch ESSCO catalog:', err);
      setError(err.message || 'Failed to load catalog');
    } finally {
      setLoading(false);
    }
  }, []);

  // The catalog is admin/staff data behind auth. Skip the fetch entirely for
  // unauthenticated visitors (e.g. public share links) so /api/essco doesn't
  // 401 and surface a spurious "Authentication required" toast.
  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setProducts([]);
      setLoading(false);
      return;
    }
    fetchCatalog();
  }, [authLoading, user, fetchCatalog]);

  const searchProducts = useCallback((query: string): EsscoProduct[] => {
    if (!query.trim()) return [];
    const lowerQuery = query.toLowerCase();
    return products.filter(p => 
      p.esscoId.toLowerCase().includes(lowerQuery) ||
      p.name.toLowerCase().includes(lowerQuery) ||
      (p.description && p.description.toLowerCase().includes(lowerQuery)) ||
      p.categories.some(c => c.name.toLowerCase().includes(lowerQuery))
    );
  }, [products]);

  const getFavorites = useCallback((): EsscoProduct[] => {
    return products.filter(p => p.isFavorite);
  }, [products]);

  const getProductById = useCallback((id: string): EsscoProduct | undefined => {
    return products.find(p => p.id === id);
  }, [products]);

  const getProductByEsscoId = useCallback((esscoId: string): EsscoProduct | undefined => {
    return products.find(p => p.esscoId === esscoId);
  }, [products]);

  const updateProduct = useCallback(async (
    id: string, 
    data: Partial<Pick<EsscoProduct, 'esscoId' | 'name' | 'description' | 'price' | 'unit' | 'isInStock' | 'isFavorite'>>
  ): Promise<EsscoProduct> => {
    const updated = await api.updateEsscoProduct(id, data);
    setProducts(prev => prev.map(p => p.id === id ? updated : p));
    return updated;
  }, []);

  const duplicateProduct = useCallback(async (id: string): Promise<EsscoProduct> => {
    const duplicated = await api.duplicateEsscoProduct(id);
    setProducts(prev => [...prev, duplicated]);
    return duplicated;
  }, []);

  const toggleFavorite = useCallback(async (id: string): Promise<void> => {
    const product = products.find(p => p.id === id);
    if (!product) return;
    await updateProduct(id, { isFavorite: !product.isFavorite });
  }, [products, updateProduct]);

  const deleteProduct = useCallback(async (id: string): Promise<void> => {
    await api.deleteEsscoProduct(id);
    setProducts(prev => prev.filter(p => p.id !== id));
  }, []);

  const refreshCatalog = useCallback(async (): Promise<void> => {
    await fetchCatalog();
  }, [fetchCatalog]);

  const value = useMemo(() => ({
    products,
    loading,
    error,
    searchProducts,
    getFavorites,
    updateProduct,
    duplicateProduct,
    toggleFavorite,
    deleteProduct,
    refreshCatalog,
    getProductById,
    getProductByEsscoId,
  }), [
    products, 
    loading, 
    error, 
    searchProducts, 
    getFavorites, 
    updateProduct,
    duplicateProduct,
    toggleFavorite, 
    deleteProduct, 
    refreshCatalog,
    getProductById,
    getProductByEsscoId,
  ]);

  return (
    <EsscoCatalogContext.Provider value={value}>
      {children}
    </EsscoCatalogContext.Provider>
  );
}

export function useEsscoCatalog() {
  const context = useContext(EsscoCatalogContext);
  if (!context) {
    throw new Error('useEsscoCatalog must be used within an EsscoCatalogProvider');
  }
  return context;
}

